import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Truncate } from '@re-dev/react-truncate'
import { sortBy } from 'lodash'
import moment from 'moment'
import { saveAs } from 'file-saver'

import { selectEmployeeByUuid } from '../payroll.selectors'
import {
  fetchEmployeeFederalTaxes,
  fetchEmployeeStateTaxes,
  fetchEmployeePaymentMethod,
  fetchEmployeePaystub,
  FETCH_EMPLOYEE_PAYSTUB_KEY,
  fetchEmployeePaystubs,
} from '../payrollActions'
import { EmployeeJobCompPanel } from './EmployeeJobCompPanel'
import { EmployeeDetailsPanel } from './EmployeeDetailsPanel'
import { formatCurrency } from '@/utils/currencyHelpers'
import { getIsFetching } from '@/reducers/fetch'
import { Select, Link, Text, Button, Alert, CardColors, Card } from '@/ui'
import { useFetchResponse, useReselector } from '@/utils/sharedHooks'
import { DATE_FORMATS } from '@/utils/dateHelpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  Gusto_EmployeeFederalTaxes,
  Gusto_EmployeePaymentMethod,
  Gusto_EmployeeStateTaxes,
} from '../generated_gusto_types'
import { TypographyStyles } from '@/styles'

export const DetailsRow = ({
  label,
  value,
}: {
  label: string | null
  value: string | undefined | null
}) => (
  <div className="flex items-start justify-between py-1">
    <Text className="w-5/12">
      <b>{label || ''}</b>
    </Text>
    <Text className="w-7/12 text-right">
      <Truncate>{value || ''}</Truncate>
    </Text>
  </div>
)

const PaymentMethodPanel = ({ uuid }: { uuid: string }) => {
  const dispatch = useAppDispatch()
  const [paymentMethod, setPaymentMethod] =
    useState<Gusto_EmployeePaymentMethod>()

  useEffect(() => {
    const fetch = async () => {
      const res = await fetchEmployeePaymentMethod(uuid)(dispatch)

      if (res) {
        setPaymentMethod(res)
      }
    }
    fetch()
  }, [dispatch, uuid])

  return (
    <Card color={CardColors.LEVEL_0}>
      <Text variant={TypographyStyles.H2}>Payment Details</Text>

      {paymentMethod && (
        <>
          <DetailsRow label="Payment Method" value={paymentMethod.type} />
          {paymentMethod.splits?.[0] && (
            <DetailsRow
              label="Bank Account"
              value={paymentMethod.splits[0].name}
            />
          )}
        </>
      )}
    </Card>
  )
}

const FederalTaxesPanel = ({ uuid }: { uuid: string }) => {
  const dispatch = useAppDispatch()
  const [federalTaxes, setFederalTaxes] = useState<Gusto_EmployeeFederalTaxes>()

  useEffect(() => {
    const fetch = async () => {
      const res = await fetchEmployeeFederalTaxes(uuid)(dispatch)
      if (res) {
        setFederalTaxes(res)
      }
    }

    fetch()
  }, [dispatch, uuid])

  return (
    <Card color={CardColors.LEVEL_0}>
      <div className="w-full">
        <Text variant={TypographyStyles.H2}>Federal Taxes</Text>
      </div>
      {federalTaxes && (
        <>
          <DetailsRow
            label="Filing Status"
            value={federalTaxes.filing_status}
          />
          <DetailsRow
            label="Multiple Jobs"
            value={federalTaxes.two_jobs ? 'Yes' : 'No'}
          />
          {/*This comes back with decimals so convert to number then string*/}
          <DetailsRow
            label="Dependents"
            value={Number(federalTaxes.dependents_amount).toString()}
          />
          <DetailsRow
            label="Other Income"
            value={formatCurrency(federalTaxes.other_income)}
          />
          <DetailsRow
            label="Deductions"
            value={formatCurrency(federalTaxes.deductions)}
          />
          <DetailsRow
            label="Extra Withholding"
            value={formatCurrency(federalTaxes.extra_withholding)}
          />
        </>
      )}
    </Card>
  )
}

const StateTaxesPanel = ({ uuid }: { uuid: string }) => {
  const dispatch = useAppDispatch()
  const [stateTaxes, setStateTaxes] = useState<Gusto_EmployeeStateTaxes>()

  useEffect(() => {
    const fetch = async () => {
      const res = await fetchEmployeeStateTaxes(uuid)(dispatch)
      if (res?.length) {
        setStateTaxes(res[0])
      }
    }

    fetch()
  }, [dispatch, uuid])

  return (
    <Card color={CardColors.LEVEL_0}>
      <div className="w-full">
        <Text variant={TypographyStyles.H2}>State Taxes</Text>
      </div>
      {stateTaxes?.questions.map((question) => {
        const answer = question.answers[0]?.value

        let answerText = ''
        if (typeof answer === 'boolean') {
          answerText = answer ? 'Yes' : 'No'
        } else if (answer !== undefined) {
          answerText = answer.toString()
        }
        return (
          <DetailsRow
            key={question.key}
            label={question.label}
            value={answerText}
          />
        )
      })}
    </Card>
  )
}

const PayStubs = ({ uuid }: { uuid: string }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const employee = useReselector(selectEmployeeByUuid, uuid)
  const [selectedPayrollUuid, setSelectedPayrollUuid] = useState<string>()

  const paystubs = useFetchResponse(fetchEmployeePaystubs, uuid)

  const isDownloadingPdf = useReselector(
    getIsFetching,
    FETCH_EMPLOYEE_PAYSTUB_KEY
  )

  const sortedPaystubs = useMemo(
    () => sortBy(paystubs, 'check_date'),
    [paystubs]
  )

  const mostRecent = sortedPaystubs[0] ? sortedPaystubs[0] : null

  // Once paystubs are fetched set the selected dropdown to latest
  useEffect(() => {
    setSelectedPayrollUuid(mostRecent?.payroll_uuid)
  }, [mostRecent?.payroll_uuid])

  const downloadPaystub = useCallback(async () => {
    if (!selectedPayrollUuid || !employee?.uuid) {
      return
    }

    const paystub = paystubs?.find(
      (paystub) => paystub.payroll_uuid === selectedPayrollUuid
    )

    if (!paystub) {
      return
    }

    const res = await fetchEmployeePaystub(
      selectedPayrollUuid,
      employee.uuid
    )(dispatch)

    if (!res) {
      return
    }

    saveAs(
      res,
      `paystub_${paystub.check_date}_${employee?.first_name}_${employee?.last_name}.pdf`
    )
  }, [
    selectedPayrollUuid,
    employee?.uuid,
    employee?.first_name,
    employee?.last_name,
    paystubs,
    dispatch,
  ])

  return (
    <Card color={CardColors.LEVEL_0}>
      <Text variant={TypographyStyles.H2}>Pay Stubs</Text>
      <br />

      {paystubs?.length ? (
        <>
          <Link href={`/payroll/history/${selectedPayrollUuid}`}>
            Most recent {mostRecent?.check_date}
          </Link>
          <br />
          <br />
          <Text variant={TypographyStyles.LABEL_MD} className="pb-2">
            Previous Pay Stubs
          </Text>

          <Select
            options={sortedPaystubs.map((paystub) => ({
              value: paystub.payroll_uuid,
              text: moment(paystub.check_date).format(
                DATE_FORMATS.DISPLAY_SHORT
              ),
            }))}
            value={selectedPayrollUuid}
            onValueChange={setSelectedPayrollUuid}
          />
          <br />

          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              disabled={!selectedPayrollUuid}
              onClick={() =>
                navigate(`/payroll/history/${selectedPayrollUuid}`)
              }
              className="w-full sm:w-1/2"
              variant="secondary"
            >
              View Details
            </Button>

            <Button
              disabled={!selectedPayrollUuid}
              onClick={downloadPaystub}
              className="w-full sm:w-1/2"
              variant="secondary"
              loading={isDownloadingPdf}
            >
              Download
            </Button>
          </div>
        </>
      ) : (
        <Alert
          title="Pay Stubs"
          description="All future employee pay stubs will display here."
        />
      )}
    </Card>
  )
}

export const JobPayTab = ({ uuid }: { uuid: string }) => {
  const employee = useReselector(selectEmployeeByUuid, uuid)

  if (!employee) {
    return null
  }

  return (
    <div className="flex flex-col gap-2 lg:flex-row">
      <div className="flex flex-col lg:w-1/2">
        <EmployeeDetailsPanel employeeUuid={employee.uuid} />
        <EmployeeJobCompPanel employeeUuid={employee.uuid} />
        <PaymentMethodPanel uuid={employee.uuid} />
        <FederalTaxesPanel uuid={employee.uuid} />
        <StateTaxesPanel uuid={employee.uuid} />
      </div>

      <div className="lg:w-1/2">
        <PayStubs uuid={uuid} />
      </div>
    </div>
  )
}
